import { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { FileText, Download, Calendar, DollarSign, Clock, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');

export default function MyTaxSlips() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [fiscalYear, setFiscalYear] = useState(new Date().getFullYear());
  const [fiscalYears, setFiscalYears] = useState([]);
  const [taxSlips, setTaxSlips] = useState([]);
  const [agentInfo, setAgentInfo] = useState(null);

  useEffect(() => {
    loadData();
  }, [fiscalYear]);

  const loadData = async () => {
    setLoading(true);
    const token = getToken();
    
    try {
      // Get available fiscal years
      const yearsRes = await axios.get(`${API}/api/accounting/reports/fiscal-years`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFiscalYears(yearsRes.data.fiscal_years || []);

      // Get agent's tax slips
      const slipsRes = await axios.get(`${API}/api/accounting/my-tax-slips?fiscal_year=${fiscalYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTaxSlips(slipsRes.data || []);

      // Get agent profile info
      const profileRes = await axios.get(`${API}/api/auth/agent-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgentInfo(profileRes.data);
    } catch (err) {
      console.error(err);
      // Don't show error for empty slips
    } finally {
      setLoading(false);
    }
  };

  const downloadT4 = async () => {
    const token = getToken();
    try {
      const response = await axios.get(
        `${API}/api/accounting/t4/generate/${user.agent_id}?fiscal_year=${fiscalYear}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `T4_${fiscalYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('T4 slip downloaded');
    } catch (err) {
      toast.error('Failed to download T4 slip');
    }
  };

  const downloadT4A = async () => {
    const token = getToken();
    try {
      const response = await axios.get(
        `${API}/api/accounting/t4a/generate/${user.agent_id}?fiscal_year=${fiscalYear}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `T4A_${fiscalYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('T4A slip downloaded');
    } catch (err) {
      toast.error('Failed to download T4A slip');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const isEmployee = agentInfo?.employment_type === 'employee';

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="my-tax-slips-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
            My Tax Slips
          </h1>
          <p className="text-slate-500 mt-1">
            Download your T4/T4A slips for tax filing
          </p>
        </div>
        <Select value={fiscalYear.toString()} onValueChange={(v) => setFiscalYear(parseInt(v))}>
          <SelectTrigger className="w-32" data-testid="fiscal-year-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {fiscalYears.map(year => (
              <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Agent Info Card */}
      {agentInfo && (
        <Card>
          <CardContent className="p-6">
            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-slate-500 uppercase">Name</p>
                <p className="font-medium text-slate-900">{agentInfo.name}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Email</p>
                <p className="font-medium text-slate-900">{agentInfo.email}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Employment Type</p>
                <Badge className={isEmployee ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}>
                  {isEmployee ? 'Employee (T4)' : 'Contractor (T4A)'}
                </Badge>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Tax Year</p>
                <p className="font-mono font-bold text-lg text-slate-900">{fiscalYear}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Download Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Available Tax Slips
          </CardTitle>
          <CardDescription>
            Download your tax slips for the {fiscalYear} tax year
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {isEmployee ? (
              // Employee - T4 Slip
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-blue-900">T4 - Statement of Remuneration Paid</h3>
                    <p className="text-sm text-blue-700">Employment income, CPP/EI contributions, and tax deducted</p>
                  </div>
                </div>
                <Button onClick={downloadT4} data-testid="download-t4-btn" className="bg-blue-600 hover:bg-blue-700">
                  <Download className="w-4 h-4 mr-2" />
                  Download T4
                </Button>
              </div>
            ) : (
              // Contractor - T4A Slip
              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-amber-900">T4A - Statement of Pension, Retirement, Annuity, and Other Income</h3>
                    <p className="text-sm text-amber-700">Self-employed commissions (Box 020)</p>
                  </div>
                </div>
                <Button onClick={downloadT4A} data-testid="download-t4a-btn" className="bg-amber-600 hover:bg-amber-700">
                  <Download className="w-4 h-4 mr-2" />
                  Download T4A
                </Button>
              </div>
            )}
          </div>

          {/* Generated Slips History */}
          {taxSlips.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-slate-700 mb-3">Previously Generated Slips</h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Year</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Generated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {taxSlips.map((slip) => (
                    <TableRow key={slip.id}>
                      <TableCell>
                        <Badge className={slip.slip_type === 'T4' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}>
                          {slip.slip_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono">{slip.fiscal_year}</TableCell>
                      <TableCell className="font-mono text-emerald-600">
                        {formatCurrency(slip.employment_income || slip.box_020_commissions || 0)}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {new Date(slip.generated_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="p-4">
          <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Important Information
          </h4>
          <ul className="text-sm text-slate-600 space-y-1">
            <li>• <strong>T4 Slips</strong> are for employees with employment income (Box 14)</li>
            <li>• <strong>T4A Slips</strong> are for self-employed contractors with commission income (Box 020)</li>
            <li>• Tax slips are required for filing your personal income tax return</li>
            <li>• If you have questions about your slip, please contact the administrator</li>
            <li>• Ensure your SIN is on file for accurate tax reporting</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
